import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Send, Mail, FileText, Loader2, Eye, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendEmail() {
  const [senders, setSenders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [selectedSender, setSelectedSender] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [additionalVariables, setAdditionalVariables] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);

  const currentTemplate = templates.find(t => t.id === selectedTemplate);
  const currentSender = senders.find(s => s.id === selectedSender);

  // Get additional variables (excluding customer_name which is handled separately)
  const getAdditionalVariables = () => {
    if (!currentTemplate) return [];
    return currentTemplate.variables.filter(v => v.toLowerCase() !== 'customer_name');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sendersRes, templatesRes] = await Promise.all([
          axios.get(`${API}/senders`),
          axios.get(`${API}/templates`)
        ]);
        setSenders(sendersRes.data.filter(s => s.is_active));
        setTemplates(templatesRes.data);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (currentTemplate) {
      const additionalVars = getAdditionalVariables();
      const vars = {};
      additionalVars.forEach(v => {
        vars[v] = additionalVariables[v] || '';
      });
      setAdditionalVariables(vars);
    }
  }, [selectedTemplate]);

  const handleAdditionalVariableChange = (key, value) => {
    setAdditionalVariables(prev => ({ ...prev, [key]: value }));
  };

  const getAllVariables = () => {
    return {
      customer_name: customerName,
      ...additionalVariables
    };
  };

  const getPreviewContent = (text) => {
    let result = text;
    const allVars = getAllVariables();
    Object.entries(allVars).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), value || `{{${key}}}`);
    });
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSender || !selectedTemplate || !recipientEmail) {
      toast.error('Please fill all required fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/emails/send`, {
        recipient_email: recipientEmail,
        sender_id: selectedSender,
        template_id: selectedTemplate,
        variables: getAllVariables()
      });
      toast.success('Email sent successfully!');
      setRecipientEmail('');
      setCustomerName('');
      setSelectedSender('');
      setSelectedTemplate('');
      setAdditionalVariables({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="send-email-page">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const additionalVars = getAdditionalVariables();

  return (
    <div className="space-y-6" data-testid="send-email-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Send Email</h1>
        <p className="text-muted-foreground mt-1">
          Enter customer details and select a template to send
        </p>
      </div>

      {senders.length === 0 || templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Setup Required</h3>
            <p className="text-muted-foreground">
              {senders.length === 0 && 'No sender emails configured. '}
              {templates.length === 0 && 'No email templates available. '}
              Please ask your manager or admin to set these up.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Compose Email
            </CardTitle>
            <CardDescription>
              Fill in the customer details to send an email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Details Section */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-primary" />
                  <Label className="text-sm font-semibold">Customer Details</Label>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Recipient Email */}
                  <div className="space-y-2">
                    <Label htmlFor="recipient">Customer Email *</Label>
                    <Input
                      id="recipient"
                      type="email"
                      placeholder="customer@example.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      required
                      data-testid="recipient-email-input"
                    />
                  </div>

                  {/* Customer First Name */}
                  <div className="space-y-2">
                    <Label htmlFor="customerName">Customer First Name</Label>
                    <Input
                      id="customerName"
                      type="text"
                      placeholder="John"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      data-testid="customer-name-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      This fills <code className="bg-background px-1 rounded">{`{{customer_name}}`}</code> in templates
                    </p>
                  </div>
                </div>
              </div>

              {/* Sender Selection */}
              <div className="space-y-2">
                <Label>From (Sender) *</Label>
                <Select value={selectedSender} onValueChange={setSelectedSender}>
                  <SelectTrigger data-testid="sender-select">
                    <SelectValue placeholder="Select sender email" />
                  </SelectTrigger>
                  <SelectContent>
                    {senders.map(sender => (
                      <SelectItem key={sender.id} value={sender.id}>
                        {sender.name} ({sender.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Email Template *</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Select email template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Template Variables (if any beyond customer_name) */}
              {currentTemplate && additionalVars.length > 0 && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <Label className="text-sm font-semibold">Additional Template Variables</Label>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {additionalVars.map(variable => (
                      <div key={variable} className="space-y-2">
                        <Label htmlFor={variable} className="text-sm">
                          <code className="font-mono text-xs bg-background px-1 py-0.5 rounded">{`{{${variable}}}`}</code>
                        </Label>
                        <Input
                          id={variable}
                          placeholder={`Enter ${variable.replace(/_/g, ' ')}`}
                          value={additionalVariables[variable] || ''}
                          onChange={(e) => handleAdditionalVariableChange(variable, e.target.value)}
                          data-testid={`variable-${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview Button */}
              {currentTemplate && (
                <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" className="w-full" data-testid="preview-btn">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Email Preview</DialogTitle>
                      <DialogDescription>
                        This is how your email will appear to the recipient
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">FROM</Label>
                        <p className="text-sm font-medium">
                          {currentSender ? `${currentSender.name} <${currentSender.email}>` : 'Select a sender'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">TO</Label>
                        <p className="text-sm font-medium">{recipientEmail || 'Enter recipient'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">SUBJECT</Label>
                        <p className="text-sm font-semibold">{getPreviewContent(currentTemplate.subject)}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">BODY</Label>
                        <div 
                          className="p-4 bg-muted rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: getPreviewContent(currentTemplate.body) }}
                        />
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={sending}
                data-testid="send-email-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
